import Select from "components/ui/Select";
import Input from "components/ui/Input";
import { useEffect, useMemo, useState } from "react";
import CashCustomerModal from "./CashCustomerModal";

import {
  useAllSalebills,
  useLastTraderSaleBillNumber,
  useOneSalebill,
} from "http-hooks/salebill/useSalebill";
import { useAllAgorders } from "http-hooks/agorder/useAgorder";
import { getBillTypeWord } from "utils/salebill";
import { toast } from "react-toastify";

import {
  SelectVendors,
  SelectCustomers,
  SelectTransporters,
  SelectPriceLists,
  SelectCndnTypes,
} from "components/ui/selectors";
import { useBill } from "hooks/bill";
import useAuth from "hooks/useAuth";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import SelectGodown from "components/ui/selectors/SelectGodown";
import { useAvailableGodowns } from "http-hooks/godown/useGodown";

const foreignCurrencyOptions = [
  { value: "INR", label: "INR — Indian Rupee" },
  { value: "USD", label: "USD — US Dollar" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "GBP", label: "GBP — British Pound" },
  { value: "AUD", label: "AUD — Australian Dollar" },
  { value: "CAD", label: "CAD — Canadian Dollar" },
  { value: "SGD", label: "SGD — Singapore Dollar" },
  { value: "AED", label: "AED — UAE Dirham" },
];

export default function SetSaleBillTop({ id, docType }) {
  // Get agttp from auth hook to determine if vendor/customer are required
  const { agttp, userGodownId } = useAuth();
  const {
    getSalebillMode,
    isForeignExportEnabled,
    isTraderSalebillAutoNumberEnabled,
  } = useCompanyPreferences();
  const salebillMode = getSalebillMode();
  const [isCashCustomerModalOpen, setIsCashCustomerModalOpen] = useState(false);
  
  // Global bill header state
  const {
    billHeader,
    selectBoxes,
    billMode,
    setBillMode,
    handleInputChange,
    handleDateChange,
    handleVendorChange,
    handleCustomerChange,
    handleTransporterChange,
    handleBillTypeChange,
    handlePricelistChange,
    handleOrderChange,
    handlePackingListChange,
    handleShowTables,
    getDocumentTypeOptions,
    populateBillFromData,
    checkSalebillDuplicate,
    setDocType,
    updateSelectBox,
    traderBillType,
    setTraderBillType,
    confirmpltransfer,
    isLoadingTableData,
    isForeignCounterparty,
  } = useBill();
  const isForeignCurrencyBill = isForeignExportEnabled();
  const requiresGodown =
    agttp === 1 && ["AGENTBILL", "CNDN"].includes(docType);
  const { data: availableGodownsData } = useAvailableGodowns(
    { noPagination: "true", agstat: "1" },
    { enabled: requiresGodown }
  );
  const availableGodowns = useMemo(
    () => {
      const godowns = availableGodownsData?.data || [];
      return userGodownId > 0
        ? godowns.filter((godown) => Number(godown.id) === userGodownId)
        : godowns;
    },
    [availableGodownsData, userGodownId]
  );
  const selectedGodown = useMemo(() => {
    const godownId = Number(billHeader.godown || 0);
    const godown = availableGodowns.find(
      (option) => Number(option.id) === godownId
    );
    return godown ? { value: godown.id, label: godown.name } : null;
  }, [availableGodowns, billHeader.godown]);

  const isTraderSale = agttp === 1 && traderBillType === "S";
  const isTraderPurchase = agttp === 1 && traderBillType === "P";
  const isAutoNumberedTraderSale =
    isTraderSale && isTraderSalebillAutoNumberEnabled();
  const { data: previousTraderSaleBill } = useLastTraderSaleBillNumber(
    !id && isAutoNumberedTraderSale
  );
  const isCashTraderBill =
    agttp === 1 && billHeader.settlementMode === "CASH";
  const showCustomer = agttp === 0 || (isTraderSale && !isCashTraderBill);
  const showVendor = agttp === 0 || isTraderPurchase;
  const hasRequiredParty = agttp === 0
    ? Boolean(selectBoxes.customer && selectBoxes.vendor)
    : isTraderSale
      ? isCashTraderBill || Boolean(selectBoxes.customer)
      : Boolean(selectBoxes.vendor);

  // Loading Salebill details for Edit
  const { data: singleSaleBillData, refetch: refetchSaleBillDetails } =
    useOneSalebill(id, { enabled: false, refetchOnWindowFocus: false });

  // Load all the orders if billtype is ORD
  const { data: { data: allAgorders } = [], refetch: refetchAgorders } =
    useAllAgorders(
      {
        ordcusid: selectBoxes.customer?.ps_Id,
        ordvendid: selectBoxes.vendor?.ps_Id,
        casestatus: 1,
      },
      false
    );

  // Load all unused packing lists if billtype is PL
  const { data: { data: plSaleBill } = [], refetch: refetchShippingInfos } =
    useAllSalebills(
      {
        cusid: selectBoxes.customer?.ps_Id,
        vendid: selectBoxes.vendor?.ps_Id,
        traderbilltyp: traderBillType,
        unused_pl: true, // Use the new filter parameter
      },
      { enabled: false, refetchOnWindowFocus: false }
    );

  // Input change handlers are now provided by the hook

  // Check if top table is filled - now handled by the hook

  const documentTypeOptions = useMemo(() => {
    return getDocumentTypeOptions(docType);
  }, [docType, getDocumentTypeOptions]);

  const handleshowTables = async () => {
    const isValid = handleShowTables(docType);

    if (!isValid) {
      return toast.error(
        "Please fill all the information above before proceeding"
      );
    }

    // console.log("tableData", selectBoxes.order, selectBoxes.pricelist.rateid);

    // Table data fetching is now handled by the parent component
  };

  const handleBillNo = async () => {
    if (billHeader.salebillno && billHeader.salebillno.trim()) {
      try {
        const result = await checkSalebillDuplicate({
          salebillno: billHeader.salebillno,
          excludeSalebillId: id || undefined,
        });

        if (result?.isDuplicate) {
          toast.error("Document number already exists");
          handleInputChange("salebillno", "");
        }
      } catch (error) {
        console.error("Error checking bill number duplicate:", error);
        toast.error("Error validating bill number");
      }
    }
  };

  // Set docType in store when component mounts or docType changes
  useEffect(() => {
    setDocType(docType);
  }, [docType, setDocType]);

  useEffect(() => {
    if (
      requiresGodown &&
      availableGodowns.length === 1 &&
      !Number(billHeader.godown)
    ) {
      handleInputChange("godown", availableGodowns[0].id);
    }
  }, [
    availableGodowns,
    billHeader.godown,
    handleInputChange,
    requiresGodown,
  ]);

  // Enforce bill mode based on company preference (salebill_mode)
  useEffect(() => {
    if (docType !== "AGENTBILL") return;
    if (salebillMode === 0) {
      setBillMode({ value: "default", label: "Default" });
    } else if (salebillMode === 1) {
      setBillMode({ value: "without_items", label: "Without Items" });
    }
  }, [salebillMode, docType, setBillMode]);

  useEffect(() => {
    if (hasRequiredParty && selectBoxes.billType) {
      if (selectBoxes.billType.value === "ORD") {
        refetchAgorders();
      } else if (selectBoxes.billType.value === "PL") {
        refetchShippingInfos();
      }
    } else {
      handleOrderChange(null);
      handlePackingListChange(null);
    }
  }, [
    selectBoxes.customer,
    selectBoxes.vendor,
    selectBoxes.billType,
    traderBillType,
    hasRequiredParty,
    refetchAgorders,
    refetchShippingInfos,
    handleOrderChange,
    handlePackingListChange,
  ]);

  useEffect(() => {
    if (["AGENTBILL"].includes(docType)) {
      handlePricelistChange(null);
    }
  }, [selectBoxes.vendor, docType, handlePricelistChange]);

  useEffect(() => {
    if (id) {
      refetchSaleBillDetails();
    }
  }, [id, refetchSaleBillDetails]);

  useEffect(() => {
    // Only populate for AGENTBILL edit mode, not for PL (shipping info)
    // PL edit is handled in SetShippingInfo component
    if (singleSaleBillData && (docType === "AGENTBILL" || docType === "CNDN")) {
      if (
        agttp === 1 &&
        ["P", "S"].includes(singleSaleBillData.traderbilltyp)
      ) {
        setTraderBillType(singleSaleBillData.traderbilltyp);
      }
      console.log({ singleSaleBillData });
      populateBillFromData(singleSaleBillData);
    }
  }, [
    agttp,
    singleSaleBillData,
    populateBillFromData,
    docType,
    setTraderBillType,
  ]);

  // totalGross, totalTax, totalQty, totalQtyPcs are now provided by the hook

  return (
    <div>
      <div className="border-b border-gray-900/10 pb-10">
        <h2 className="text-3xl font-semibold leading-7 text-gray-900 text-center">
          {id ? "Edit " : ""}
          {docType === "PL" ? "Challan" : docType === "CNDN" ? "Credit / Debit Note" : "Manufacturer/Supplier Bill"}
        </h2>
        
        {/* Case Diversion Indicator */}
        {confirmpltransfer && selectBoxes.transferCustomer && selectBoxes.billType?.value === "PL" && (
          <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-800">
                  <span className="font-bold">Case Diverted:</span> This packing list will be transferred from{" "}
                  <span className="font-semibold">{selectBoxes.customer?.name}</span> to{" "}
                  <span className="font-semibold">{selectBoxes.transferCustomer?.name}</span>
                </p>
                <p className="mt-1 text-xs text-yellow-700">
                  Bill type will change from PL to STK (Stock) after submission.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {docType === "AGENTBILL" && salebillMode === 2 && (
          <div className="mt-4 w-full sm:w-1/2 lg:w-1/4 mx-auto">
            <Select
              value={billMode}
              onChange={setBillMode}
              options={[
                { value: "default", label: "Default" },
                { value: "without_items", label: "Without Items" },
              ]}
              placeholder="Select bill mode"
              isClearable={false}
              isSearchable={false}
              showLabel={false}
            />
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {docType === "CNDN" && (
            <Select
              label="Note Type"
              value={selectBoxes.billType}
              onChange={(option) => {
                handleBillTypeChange(option);
                if (agttp === 1) {
                  setTraderBillType(option?.value === "VDN" ? "P" : "S");
                }
              }}
              options={documentTypeOptions}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isRequired={true}
              isDisabled={!!id}
            />
          )}
          {docType === "CNDN" && (
            <SelectCndnTypes
              label="CN/DN Type"
              value={selectBoxes.cndnType || null}
              onChange={(val) => {
                updateSelectBox("cndnType", val);
                handleInputChange("cndntype", val?.name || "");
                if (val?.name === "Direct(Without Item)") {
                  handleInputChange("billdefault", 0);
                } else {
                  handleInputChange("billdefault", 1);
                }
              }}
              isRequired={true}
              initialValue={billHeader.cndntype ? { name: billHeader.cndntype } : null}
              showAddButton={false}
            />
          )}
          {agttp === 1 && (
            <Select
              label="Trader Bill Direction"
              value={{
                value: traderBillType,
                label: traderBillType === "S" ? "Sale" : "Purchase",
              }}
              onChange={(value) => setTraderBillType(value?.value)}
              options={[
                { value: "S", label: "Sale" },
                { value: "P", label: "Purchase" },
              ]}
              isClearable={false}
              isSearchable={false}
              isDisabled={!!id || docType === "CNDN"}
            />
          )}
          {requiresGodown && availableGodowns.length > 1 && (
            <SelectGodown
              label="Godown *"
              value={selectedGodown}
              onChange={(option) =>
                handleInputChange("godown", option?.value || 0)
              }
              includeAllGodowns={false}
              isClearable={false}
              isRequired
            />
          )}
          {agttp === 1 && docType === "AGENTBILL" && (
            <Select
              label="Settlement"
              value={{
                value: billHeader.settlementMode || "CREDIT",
                label:
                  billHeader.settlementMode === "CASH" ? "Cash" : "Credit",
              }}
              onChange={(value) => {
                handleInputChange("settlementMode", value?.value || "CREDIT");
                if (value?.value === "CASH" && isTraderSale) {
                  handleCustomerChange(null);
                }
              }}
              options={[
                { value: "CREDIT", label: "Credit" },
                { value: "CASH", label: "Cash" },
              ]}
              isClearable={false}
              isSearchable={false}
              isDisabled={!!id}
            />
          )}
          {showCustomer && (
            <SelectCustomers
              value={selectBoxes.customer}
              onChange={handleCustomerChange}
              className=""
              isRequired={true}
              isDisabled={!!id}
            />
          )}
          {isForeignCurrencyBill && (
            <>
              <Select
                label="Invoice Currency"
                value={foreignCurrencyOptions.find(
                  (option) => option.value === billHeader.currency_code
                )}
                onChange={(option) =>
                  handleInputChange("currency_code", option?.value || "USD")
                }
                options={foreignCurrencyOptions}
                isClearable={false}
                isSearchable={false}
                isDisabled={
                  !isForeignCounterparty || (!!id && billHeader.adj > 0)
                }
              />
            </>
          )}
          {showVendor && (
            <SelectVendors
              value={selectBoxes.vendor}
              onChange={handleVendorChange}
              className=""
              isRequired={true}
              isDisabled={!!id}
            />
          )}
          {isCashTraderBill && isTraderSale && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium leading-6 text-gray-900">Cash Customer</span>
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setIsCashCustomerModalOpen(true)}
                disabled={!!id}
              >
                {billHeader.cashCustomer?.cname || "Add cash customer details"}
              </button>
            </div>
          )}
          <Input
            label={`${
              !id && isAutoNumberedTraderSale
                ? "Previous Bill"
                : docType === "AGENTBILL"
                ? "Bill"
                : docType === "PL"
                ? "Challan"
                : docType === "CNDN"
                ? "Note"
                : "Doc"
            } No.`}
            name="salebillno"
            id="salebillno"
            value={
              !id && isAutoNumberedTraderSale
                ? previousTraderSaleBill?.lastSaleBillNumber || ""
                : billHeader.salebillno
            }
            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
            onBlur={isAutoNumberedTraderSale ? undefined : handleBillNo}
            autoComplete="salebillno"
            required={!isAutoNumberedTraderSale}
            isRequired={!isAutoNumberedTraderSale}
            disabled={
              isAutoNumberedTraderSale || (!!id && docType === "PL")
            }
          />
          <Input
            label={`${
              docType === "AGENTBILL"
                ? "Bill"
                : docType === "PL"
                ? "Challan"
                : docType === "CNDN"
                ? "Note"
                : "Doc"
            } Date`}
            type="date"
            name="salebilldate"
            id="salebilldate"
            value={billHeader.salebilldate}
            onChange={handleDateChange}
            now={true}
            withinFY={true}
            required
            isRequired={true}
          />
          {docType !== "CNDN" && (
            <SelectTransporters
              value={selectBoxes.transporter}
              onChange={handleTransporterChange}
              className=""
            />
          )}
          {docType !== "CNDN" && (
            <Input
              label="Despatched At"
              name="orddespatch"
              id="orddespatch"
              value={billHeader.orddespatch}
              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
              autoComplete="orddespatch"
            />
          )}
          {docType !== "CNDN" && (
            <Select
              label={
                docType === "AGENTBILL"
                  ? "Bill Type"
                  : docType === "PL"
                  ? "Challan Type"
                  : "Doc Type"
              }
              value={selectBoxes.billType}
              onChange={handleBillTypeChange}
              options={documentTypeOptions}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isRequired={true}
              isDisabled={!!id}
            />
          )}
          {billHeader.billtype &&
            !["DIR", "STK", "VCN", "VDN"].includes(billHeader.billtype) && (
              <div className="">
                <label
                  htmlFor="vendid"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  {getBillTypeWord(billHeader.billtype)}
                </label>
                {billHeader.billtype === "ORD" ? (
                  <div className="mt-2">
                    <Select
                      value={selectBoxes.order}
                      onChange={handleOrderChange}
                      options={allAgorders}
                      getOptionValue={(option) => option.ordno}
                      getOptionLabel={(option) => option.ordno}
                      isMulti={true}
                      placeholder="Select orders..."
                    />
                  </div>
                ) : (
                  <div className="mt-2">
                    <Select
                      placeholder={"Select Packing Lists"}
                      value={selectBoxes.packingList}
                      onChange={handlePackingListChange}
                      options={plSaleBill}
                      getOptionValue={(plSaleBill) => plSaleBill.salebillid}
                      getOptionLabel={(plSaleBill) => plSaleBill.salebillno}
                      isMulti={true}
                    />
                  </div>
                )}
              </div>
            )}
          {["AGENTBILL"].includes(docType) ? (
            <>
              <Input
                label="Scheme Bill"
                type="checkbox"
                value={!!billHeader.is_scheme}
                onChange={(e) => handleInputChange("is_scheme", e.target.checked ? 1 : 0)}
                disabled={!!id}
              />
              {!isTraderSale && (
                <SelectPriceLists
                  label="Pricelist Date"
                  value={selectBoxes.pricelist}
                  onChange={handlePricelistChange}
                  placeholder={
                    selectBoxes.vendor ? "Select a date" : "Select a vendor"
                  }
                  vendorId={selectBoxes.vendor?.ps_Id}
                  showAddButton={false}
                />
              )}
              {!isCashTraderBill && (
                <Input
                  label="Due Date"
                  type="date"
                  name="salebillduedate"
                  id="salebillduedate"
                  value={billHeader.salebillduedate}
                  onChange={(e) =>
                    handleInputChange(e.target.name, e.target.value)
                  }
                  autoComplete="salebillduedate"
                />
              )}
              <Input
                label="LR No"
                name="lrno"
                id="lrno"
                value={billHeader.lrno}
                onChange={(e) =>
                  handleInputChange(e.target.name, e.target.value)
                }
                autoComplete="lrno"
              />
              <Input
                label="LR Date"
                type="date"
                name="lr_date"
                id="lr_date"
                value={billHeader.lr_date}
                onChange={(e) =>
                  handleInputChange(e.target.name, e.target.value)
                }
                autoComplete="lr_date"
              />
            </>
          ) : null}
        </div>
        <div className="mt-6 flex items-center justify-center gap-x-6">
          <button
            type="button"
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleshowTables}
            disabled={isLoadingTableData}
          >
            {isLoadingTableData ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : (
              "Update"
            )}
          </button>
        </div>
      </div>
      <CashCustomerModal
        open={isCashCustomerModalOpen}
        onClose={() => setIsCashCustomerModalOpen(false)}
        value={{ cname: billHeader.cashCustomerName, ...billHeader.cashCustomer }}
        onSave={(cashCustomer) => {
          handleInputChange("cashCustomer", cashCustomer);
          handleInputChange("cashCustomerName", cashCustomer.cname);
        }}
      />
    </div>
  );
}
